import { ReactElement, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { useQuery } from 'react-query'
import { toast } from 'react-toastify'
import { Box, Checkbox, FormControlLabel } from '@mui/material'
import dayjs from 'dayjs'
import { Modal } from 'components'
import Dropdown from 'components/ui/Dropdown/Dropdown'
import LoadingOverlay from 'components/ui/Loading/LoadingOverlay'
import { AppDispatch, RootState, setSelectedWheatCode } from '../../../store'
import { useDeliveriesService, useWheatCodesService } from '../../../services'
import { TWheatCode } from '../../../types'
import { ModalBody, ModalSubTitle, ModalTitleWrapper } from './styled'
import { CrudAction, Entity, usePermissions } from '../../../utils/hooks'
import {
  defaultFilterParams,
  IFilterParams,
} from '../../../types/models/FilterSort'
import { defaultNirResultsWheatCodeFilters } from './defaultNirResultsWheatCodeFilters'

const useNirResults = (): [
  ReactElement,
  (
    deliveryId: string,
    deliveryName: string,
    deliveryDate: string,
    deliveryWheatCode?: number
  ) => void,
] => {
  const { checkPermission } = usePermissions()
  const [isOpen, setIsOpen] = useState<boolean>(false)
  const [modalTitle, setModalTitle] = useState<string | null>(null)
  const [modalDeliveryDate, setModalDeliveryDate] = useState<string | null>(
    null
  )
  const [currentDeliveryId, setCurrentDeliveryId] = useState<string | null>(
    null
  )
  const dispatch = useDispatch<AppDispatch>()
  const navigate = useNavigate()
  const selectedWheatCode = useSelector(
    (state: RootState) => state.shipments.selectedWheatCode
  )

  const { getWheatCodes } = useWheatCodesService()
  const { setConsistency } = useDeliveriesService()

  const [filterParams, setFilterParams] = useState<IFilterParams>(
    defaultNirResultsWheatCodeFilters()
  )
  // TODO in future address this in be
  const disablePagination = { currentPage: 0, itemsPerPage: 100 }
  const { data: wheatCodes } = useQuery({
    queryKey: ['wheat_codes_list', filterParams],
    queryFn: () => getWheatCodes(filterParams, disablePagination),
    retry: false,
  })

  const [existingDeliveryWheatCode, setExistingDeliveryWheatCode] = useState<
    string | undefined
  >()

  const [showAllSelected, setShowAllSelected] = useState(false)

  useEffect(() => {
    setShowAllSelected(!!existingDeliveryWheatCode)
  }, [existingDeliveryWheatCode])

  useEffect(() => {
    if (showAllSelected) {
      setFilterParams(defaultFilterParams)
    } else {
      setFilterParams(defaultNirResultsWheatCodeFilters())
    }
  }, [showAllSelected])

  const handleSetCode = (wheatCode: string) => {
    dispatch(setSelectedWheatCode(wheatCode))
  }

  const [isLoading, setIsLoading] = useState<boolean>(false)

  const handleProcessWheatCode = async () => {
    if (
      !currentDeliveryId ||
      (!selectedWheatCode && !existingDeliveryWheatCode)
    ) {
      return
    }
    setIsLoading(true)
    try {
      const response = await setConsistency(
        currentDeliveryId.toString(),
        existingDeliveryWheatCode || selectedWheatCode
      )

      if (response?.result?.wheat_code_id) {
        navigate(
          `/shipments/incoming-deliveries/${currentDeliveryId}/${response?.result?.wheat_code_id}`
        )
      } else {
        toast.error('Error while processing delivery')
      }
    } catch (error) {
      toast.error('Error while processing delivery')
    } finally {
      setIsLoading(false)
      setIsOpen(false)
      setExistingDeliveryWheatCode(undefined)
    }
  }

  const wheatCodeOptions =
    wheatCodes?.results?.map((code: TWheatCode) => ({
      value: code.id.toString(),
      label: code.code,
    })) || []

  return [
    <Box>
      <Modal
        isOpen={isOpen}
        onClose={() => {
          setIsOpen(false)
          setExistingDeliveryWheatCode(undefined)
        }}
        onConfirm={handleProcessWheatCode}
        isLoading={isLoading}
        title="New NIR test results"
        confirmButtonDisabled={!selectedWheatCode && !existingDeliveryWheatCode}
      >
        <ModalBody>
          <ModalTitleWrapper>
            <ModalSubTitle>
              NIR Sample Date{' '}
              {dayjs(modalDeliveryDate).format('DD.MM.YYYY HH:mm')}
            </ModalSubTitle>
            <ModalSubTitle>{modalTitle}</ModalSubTitle>
          </ModalTitleWrapper>
          <Box>
            <FormControlLabel
              control={<Checkbox />}
              label="Show All"
              checked={showAllSelected}
              onChange={(_selected, checked) => {
                setShowAllSelected(checked)
              }}
            />
          </Box>
          <Dropdown
            label="Select Wheat Code"
            options={wheatCodeOptions}
            value={existingDeliveryWheatCode || selectedWheatCode || undefined}
            onChange={(event) => {
              if (existingDeliveryWheatCode) {
                setExistingDeliveryWheatCode(event.target.value)
              }

              handleSetCode(event.target.value)
            }}
            disabled={!checkPermission(Entity.DELIVERY, CrudAction.UPDATE)}
          />
        </ModalBody>
      </Modal>
      <LoadingOverlay open={isLoading} />
    </Box>,
    (
      deliveryId: string,
      deliveryName: string,
      deliveryDate: string,
      deliveryWheatCode
    ) => {
      setCurrentDeliveryId(deliveryId)
      setModalTitle(deliveryName)
      setModalDeliveryDate(deliveryDate)
      if (deliveryWheatCode) {
        setExistingDeliveryWheatCode(deliveryWheatCode.toString())
      }
      setIsOpen(true)
    },
  ]
}

export default useNirResults
